import {
  type UnknownRecord,
  literalAt,
  exactObject,
  requestIdAt,
  fail,
  objectAt,
  hasOwn,
  identifierAt
} from './primitives';
import {
  BRIDGE_CHANNEL,
  BRIDGE_VERSION,
  type GuestRequest,
  isBridgeCommand,
  type HostResponse,
  type BridgeCommand,
  type HostEvent,
  isBridgeEvent,
  type HostHandshake,
  type HostMessage
} from '../protocol';
import { REQUEST_VALIDATORS, EVENT_VALIDATORS } from './commands';
import { parseBridgeError, parseStateStamp } from './context';

function validateEnvelopeBase(
  object: UnknownRecord,
  path: string,
  direction: 'guest-to-host' | 'host-to-guest',
  kind: 'request' | 'response' | 'event' | 'handshake'
): void {
  literalAt(object.channel, BRIDGE_CHANNEL, `${path}.channel`);
  literalAt(object.version, BRIDGE_VERSION, `${path}.version`);
  literalAt(object.direction, direction, `${path}.direction`);
  literalAt(object.kind, kind, `${path}.kind`);
}

export function parseGuestRequest(value: unknown, path = '$'): GuestRequest {
  const object = exactObject(value, path, [
    'channel',
    'version',
    'direction',
    'kind',
    'requestId',
    'command',
    'payload'
  ]);
  validateEnvelopeBase(object, path, 'guest-to-host', 'request');
  requestIdAt(object.requestId, `${path}.requestId`);
  if (!isBridgeCommand(object.command)) fail(`${path}.command`, 'unknown command');
  REQUEST_VALIDATORS[object.command].payload(object.payload, `${path}.payload`);
  return value as GuestRequest;
}

export function parseHostResponse(value: unknown, path = '$'): HostResponse {
  const candidate = objectAt(value, path);
  if (typeof candidate.ok !== 'boolean') {
    fail(`${path}.ok`, 'expected a boolean response discriminator');
  }
  const object = exactObject(value, path, [
    'channel',
    'version',
    'direction',
    'kind',
    'requestId',
    'command',
    'ok',
    candidate.ok ? 'result' : 'error'
  ]);
  validateEnvelopeBase(object, path, 'host-to-guest', 'response');
  requestIdAt(object.requestId, `${path}.requestId`);
  if (!isBridgeCommand(object.command)) fail(`${path}.command`, 'unknown command');
  if (object.ok === true) {
    REQUEST_VALIDATORS[object.command].result(object.result, `${path}.result`);
  } else {
    parseBridgeError(object.error, `${path}.error`);
  }
  return value as HostResponse;
}

export function parseHostResponseFor<C extends BridgeCommand>(
  request: GuestRequest<C>,
  value: unknown,
  path = '$'
): HostResponse<C> {
  const response = parseHostResponse(value, path);
  if (response.requestId !== request.requestId) {
    fail(`${path}.requestId`, 'does not match the request');
  }
  if (response.command !== request.command) {
    fail(`${path}.command`, 'does not match the request');
  }
  return response as HostResponse<C>;
}

export function parseHostEvent(value: unknown, path = '$'): HostEvent {
  const object = exactObject(
    value,
    path,
    ['channel', 'version', 'direction', 'kind', 'event', 'payload'],
    ['stamp']
  );
  validateEnvelopeBase(object, path, 'host-to-guest', 'event');
  if (!isBridgeEvent(object.event)) fail(`${path}.event`, 'unknown event');
  EVENT_VALIDATORS[object.event](object.payload, `${path}.payload`);
  if (
    object.event !== 'bootstrap.updated' &&
    object.event !== 'sidebar.navigate' &&
    object.event !== 'learning.changed' &&
    object.event !== 'rules.changed'
  ) {
    parseStateStamp(object.stamp, `${path}.stamp`);
  } else if (hasOwn(object, 'stamp')) parseStateStamp(object.stamp, `${path}.stamp`);
  return value as HostEvent;
}

export function parseHostHandshake(value: unknown, path = '$'): HostHandshake {
  const object = exactObject(value, path, ['channel', 'version', 'direction', 'kind', 'payload']);
  validateEnvelopeBase(object, path, 'host-to-guest', 'handshake');
  const payload = exactObject(object.payload, `${path}.payload`, ['hostInstanceId']);
  identifierAt(payload.hostInstanceId, `${path}.payload.hostInstanceId`);
  return value as HostHandshake;
}

export function parseHostMessage(value: unknown, path = '$'): HostMessage {
  const object = objectAt(value, path);
  if (object.kind === 'response') return parseHostResponse(value, path);
  if (object.kind === 'event') return parseHostEvent(value, path);
  if (object.kind === 'handshake') return parseHostHandshake(value, path);
  fail(`${path}.kind`, 'expected response, event, or handshake');
}

export interface BridgeMessageEventLike {
  readonly data: unknown;
  readonly origin: string;
  readonly source: unknown;
}

export interface TrustedBridgePeer {
  readonly origin: string;
  readonly source: unknown;
}

function trustedMessageData(
  event: BridgeMessageEventLike,
  peer: TrustedBridgePeer,
  path: string
): unknown {
  if (
    peer.source === null ||
    peer.source === undefined ||
    (typeof peer.source !== 'object' && typeof peer.source !== 'function')
  ) {
    fail(`${path}.source`, 'the expected source window must be present');
  }
  if (peer.origin.length === 0 || peer.origin === '*') {
    fail(`${path}.origin`, 'the expected origin must be exact');
  }
  if (event.source !== peer.source) fail(`${path}.source`, 'unexpected source window');
  if (event.origin !== peer.origin) fail(`${path}.origin`, 'unexpected origin');
  return event.data;
}

export function parseGuestRequestMessage(
  event: BridgeMessageEventLike,
  peer: TrustedBridgePeer
): GuestRequest {
  return parseGuestRequest(trustedMessageData(event, peer, '$event'), '$event.data');
}

export function parseHostMessageEvent(
  event: BridgeMessageEventLike,
  peer: TrustedBridgePeer
): HostMessage {
  return parseHostMessage(trustedMessageData(event, peer, '$event'), '$event.data');
}
